import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { avanzoApi } from "@/lib/avanzoApi";
import PaesImage from "@/components/PaesImage";
import PostEvalBuddy from "./PostEvalBuddy";

/**
 * /avanzo/simulacro — Simulacro Final (Fase 7).
 *
 * Estados: loading | locked | before_window | intro | in_progress
 *          | completed | reinforcement_only | exam_day | error
 */
const SUBJECT_LABEL = {
  matematica: "Precálculo",
  ciencias:   "Ciencia y Tecnología",
  sociales:   "Ciudadanía y Valores",
  lenguaje:   "Lenguaje y Literatura",
};

const LABEL_TO_KEY = Object.fromEntries(
  Object.entries(SUBJECT_LABEL).map(([k, v]) => [v, k])
);

export default function AvanzoSimulation() {
  const [phase, setPhase] = useState("loading");
  const [gate, setGate]   = useState(null);
  const [result, setResult] = useState(null);
  const [current, setCurrent] = useState(null);
  const [choice, setChoice] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startTsRef = useRef(null);
  const submittingRef = useRef(false);

  const refreshStatus = useCallback(async () => {
    setPhase("loading");
    try {
      const r = await avanzoApi.finalSimulationStatus();
      const st = r.data || {};
      setGate(st.gate || null);
      if (st.gate?.already_completed) {
        setResult(st.result || null);
        setPhase("completed");
        return;
      }
      if (st.gate?.in_progress) {
        await enterExecution(); return;
      }
      if (st.gate?.unlocked) {
        setPhase("intro"); return;
      }
      // Sin Simulacro 1 completado, el siguiente paso es el Simulacro 1.
      if (st.gate?.reason === "no_simulation_1_completed") {
        setPhase("needs_s1"); return;
      }
      const win = st.gate?.window;
      if (win?.phase === "before_window" && !win?.allowed) setPhase("before_window");
      else if (win?.phase === "reinforcement_only") setPhase("reinforcement_only");
      else if (win?.phase === "exam_day")     setPhase("exam_day");
      else setPhase("locked");
    } catch (e) {
      setError(e); setPhase("error");
    }
  }, []);

  const enterExecution = useCallback(async () => {
    try {
      const cur = await avanzoApi.finalSimulationCurrent();
      const data = cur.data || {};
      if (data.finished) {
        const rr = await avanzoApi.finalSimulationResult();
        setResult(rr.data); setPhase("completed"); return;
      }
      setCurrent(data);
      const startedIso = data.started_at
        || current?.started_at || new Date().toISOString();
      startTsRef.current = new Date(startedIso).getTime();
      setPhase("in_progress");
    } catch (e) {
      setError(e); setPhase("error");
    }
  }, [current]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  useEffect(() => {
    if (phase !== "in_progress" || !startTsRef.current) return;
    const id = setInterval(() => {
      setElapsed(Math.max(0, Math.floor(
        (Date.now() - startTsRef.current) / 1000,
      )));
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const startRun = async () => {
    setPhase("loading");
    try {
      await avanzoApi.finalSimulationStart();
      await enterExecution();
    } catch (e) {
      setError(e); setPhase("error");
    }
  };

  const confirmAndContinue = async () => {
    if (!choice || submittingRef.current || !current?.item) return;
    submittingRef.current = true;
    try {
      const rt = startTsRef.current
        ? (Date.now() - startTsRef.current) / 1000 : null;
      const r = await avanzoApi.finalSimulationAnswer({
        run_id: current.run_id, item_id: current.item.item_id,
        selected_answer: choice, response_time_seconds: rt,
      });
      const data = r.data || {};
      if (data.finished) {
        const rr = await avanzoApi.finalSimulationResult();
        setResult(rr.data); setPhase("completed"); return;
      }
      setChoice(null);
      await enterExecution();
    } catch (e) {
      setError(e); setPhase("error");
    } finally {
      submittingRef.current = false;
    }
  };

  if (phase === "loading") {
    return <div className="avz-empty" data-testid="avz-sf-loading">
      Cargando Simulacro Final…
    </div>;
  }
  if (phase === "error") {
    return (
      <div className="avz-card" data-testid="avz-sf-error">
        <h2 className="avz-h2">No pudimos cargar tu Simulacro Final.</h2>
        {error?.response?.data?.detail === "simulation_bank_unavailable" && (
          <p className="avz-mute avz-mt-4" data-testid="avz-sf-error-detail">
            El Simulacro Final no está disponible en este momento. Tu intento no se consumió; vuelve a intentarlo más tarde.
          </p>
        )}
        <button className="avz-btn avz-btn-primary avz-mt-4"
                onClick={refreshStatus} data-testid="avz-sf-retry">
          Reintentar
        </button>
      </div>
    );
  }
  if (phase === "needs_s1") return <Navigate to="/avanzo/simulacro-1" replace />;
  if (phase === "before_window") {
    return <WindowCard testid="avz-sf-before-window"
      title="Aún no es el momento"
      body="Continúa con tu preparación. Tu Simulacro Final se habilitará en la ventana recomendada."
    />;
  }
  if (phase === "reinforcement_only") {
    return <WindowCard testid="avz-sf-reinforcement-only"
      title="Hoy es día de refuerzo"
      body="Hoy nos enfocaremos en refuerzo final y preparación ligera."
    />;
  }
  if (phase === "exam_day") {
    return <WindowCard testid="avz-sf-exam-day"
      title="Hoy es AVANZO"
      body="Confía en tu preparación."
    />;
  }
  if (phase === "locked") return <LockedCard gate={gate} />;
  if (phase === "intro")  return <IntroCard onStart={startRun} />;
  if (phase === "completed") return (
    <>
      {result?.attempt_window === "early" && (
        <p className="avz-mute avz-mt-4" data-testid="avz-sf-second-attempt-note">
          Este fue tu intento anticipado. Tendrás un segundo intento en el período
          recomendado, del 18 al 21 de octubre.
        </p>
      )}
      <ResultCard result={result} />
    </>
  );

  const item = current?.item || {};
  const idx = Number(current?.current_index || 0);
  const total = Number(current?.total || 40);
  return (
    <div data-testid="avz-sf-exec">
      <div className="avz-row-between avz-mt-4" style={{ marginBottom: 10 }}>
        <span className="avz-mute" data-testid="avz-sf-position">
          Actividad {idx + 1} de {total}
        </span>
        <span className="avz-chip" data-testid="avz-sf-elapsed">
          {formatElapsed(elapsed)}
        </span>
      </div>
      <section className="avz-card">
        {item.contexto_estimulo && (
          <p className="avz-lead" data-testid="avz-sf-context">
            {item.contexto_estimulo}
          </p>
        )}
        <PaesImage item={item} />
        <h2 className="avz-h2 avz-mt-4" data-testid="avz-sf-question">
          {item.pregunta}
        </h2>
        <div className="avz-stack avz-mt-4" role="radiogroup"
             aria-label="Opciones de respuesta">
          {["A", "B", "C", "D"].map((letter) => (
            <button key={letter} type="button" className="avz-option"
                    aria-pressed={choice === letter}
                    onClick={() => setChoice(letter)}
                    data-testid={`avz-sf-option-${letter}`}>
              <span className="avz-option-letter">{letter}</span>
              <span>{(item.opciones || {})[letter]}</span>
            </button>
          ))}
        </div>
        <div className="avz-row-between avz-mt-8">
          <span
            className="avz-mute"
            data-testid="avz-sf-subject"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <span
              className="avz-subject-dot"
              aria-hidden="true"
              style={{ background: `var(--avz-subject-${item.materia})` }}
            />
            {SUBJECT_LABEL[item.materia] || item.materia}
          </span>
          <button className="avz-btn avz-btn-primary"
                  disabled={!choice || submittingRef.current}
                  onClick={confirmAndContinue}
                  data-testid="avz-sf-confirm">
            Confirmar y continuar
          </button>
        </div>
      </section>
    </div>
  );
}

function WindowCard({ testid, title, body }) {
  return (
    <div className="avz-card" data-testid={testid}>
      <div className="avz-mute">Simulacro Final</div>
      <h1 className="avz-h1" data-testid={`${testid}-title`}>{title}</h1>
      <p className="avz-lead avz-mt-4" data-testid={`${testid}-body`}>{body}</p>
      <div className="avz-row avz-mt-8">
        <Link to="/avanzo" className="avz-btn avz-btn-primary">
          Volver a Inicio
        </Link>
      </div>
    </div>
  );
}

function LockedCard({ gate }) {
  const sessions = Number(gate?.sessions_after_s1 || 0);
  const minS = Number(gate?.min_sessions_after_s1 || 4);
  const hasS1 = !!gate?.s1_completed_at;
  const subj = Number(gate?.subjects_practiced_ever || 0);
  const minSubj = Number(gate?.min_subjects_practiced || 4);
  return (
    <div className="avz-card" data-testid="avz-sf-locked">
      <div className="avz-mute">Simulacro Final</div>
      <h1 className="avz-h1" data-testid="avz-sf-locked-title">
        Tu Simulacro Final se habilitará pronto
      </h1>
      <p className="avz-lead avz-mt-4">
        Continúa con tu preparación. Cuando hayas completado los pasos
        que te faltan y estemos dentro de la ventana recomendada, tu
        Simulacro Final estará listo.
      </p>
      <ul className="avz-mt-8">
        <li data-testid="avz-sf-check-s1">
          {hasS1 ? "✅" : "○"} Simulacro 1 completado
        </li>
        <li data-testid="avz-sf-check-sessions">
          {sessions >= minS ? "✅" : "○"} Training posterior:{" "}
          <span className="avz-strong">{Math.min(sessions, minS)} de {minS} sesiones</span>
        </li>
        <li data-testid="avz-sf-check-subjects">
          {subj >= minSubj ? "✅" : "○"} Materias practicadas:{" "}
          <span className="avz-strong">{Math.min(subj, minSubj)} de {minSubj}</span>
        </li>
      </ul>
      <div className="avz-row avz-mt-8">
        <Link to="/avanzo/entrenar" className="avz-btn avz-btn-primary">
          Continuar practicando
        </Link>
      </div>
    </div>
  );
}

function IntroCard({ onStart }) {
  return (
    <div className="avz-card" data-testid="avz-sf-intro">
      <div className="avz-mute">Simulacro Final</div>
      <h1 className="avz-h1" data-testid="avz-sf-intro-title">
        Tu simulacro previo a AVANZO
      </h1>
      <p className="avz-lead avz-mt-4">
        Responderás 40 actividades, 10 por cada área. Este simulacro te
        ayudará a conocer cómo llegas a la etapa final de preparación.
      </p>
      <ul className="avz-mt-4 avz-mute">
        <li>· 40 actividades</li>
        <li>· 4 áreas</li>
        <li>· Aproximadamente 60–75 minutos</li>
      </ul>
      <p className="avz-mute avz-mt-4">
        Busca un lugar tranquilo y procura completar el simulacro sin
        interrupciones.
      </p>
      <button className="avz-btn avz-btn-primary avz-mt-8"
              onClick={onStart} data-testid="avz-sf-start">
        Comenzar Simulacro Final
      </button>
    </div>
  );
}

function ResultCard({ result }) {
  if (!result) return <div className="avz-empty">Cargando resultado…</div>;
  const band = result.band || {};
  const cmp = result.comparison;
  return (
    <div className="avz-card" data-testid="avz-sf-result">
      <div className="avz-mute">Simulacro Final</div>
      <h1 className="avz-h1" data-testid="avz-sf-result-title">
        Resultado de tu Simulacro Final
      </h1>
      <p className="avz-h2 avz-mt-4" data-testid="avz-sf-result-score">
        {result.total_correct} de {result.total_items}
        <span className="avz-mute"> · </span>
        {Math.round(result.percentage)}%
      </p>
      <p className="avz-lead avz-mt-4" data-testid="avz-sf-result-band">
        {band.label}
      </p>
      <p className="avz-mute avz-mt-4">
        Este resultado refleja tu desempeño observado en esta etapa
        de preparación y nos ayudará a orientar tu refuerzo final.
      </p>

      <div className="avz-mt-8">
        <div className="avz-h3">Resultados por materia</div>
        <ul className="avz-mt-4" style={{ listStyle: "none", padding: 0 }}>
          {Object.entries(result.subject_results || {}).map(([label, s]) => {
            const key = LABEL_TO_KEY[label];
            return (
              <li
                key={label}
                data-subject={key}
                style={{
                  borderLeft: key ? `4px solid var(--avz-subject-${key})` : "none",
                  paddingLeft: key ? 12 : 0,
                  margin: "10px 0",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  {key && (
                    <span
                      className="avz-subject-dot"
                      aria-hidden="true"
                      style={{ background: `var(--avz-subject-${key})` }}
                    />
                  )}
                  <span className="avz-strong">{label}</span>
                </span>
                <span className="avz-mute"> · </span>
                {s.correct} de {s.attempts}
              </li>
            );
          })}
        </ul>
      </div>

      {cmp && (
        <div className="avz-mt-8" data-testid="avz-sf-comparison">
          <div className="avz-h3">Comparación con tu Simulacro 1</div>
          <p className="avz-lead avz-mt-4">
            {cmp.global_message}{" "}
            <span className="avz-mute">
              (S1: {cmp.s1_total}/40 · Final: {cmp.final_total}/40)
            </span>
          </p>
          <ul className="avz-mt-4" style={{ listStyle: "none", padding: 0 }}>
            {Object.entries(cmp.by_subject || {}).map(([label, s]) => {
              const key = LABEL_TO_KEY[label];
              return (
                <li
                  key={label}
                  data-subject={key}
                  style={{
                    borderLeft: key ? `4px solid var(--avz-subject-${key})` : "none",
                    paddingLeft: key ? 12 : 0,
                    margin: "10px 0",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {key && (
                      <span
                        className="avz-subject-dot"
                        aria-hidden="true"
                        style={{ background: `var(--avz-subject-${key})` }}
                      />
                    )}
                    <span className="avz-strong">{label}</span>
                  </span>
                  <span className="avz-mute"> · </span>
                  S1: {s.s1_correct}/10 · Final: {s.final_correct}/10
                  {" "}{s.trend === "up" ? "↑" : s.trend === "down" ? "↓" : "→"}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="avz-row avz-mt-8">
        <Link to="/avanzo/entrenar" className="avz-btn avz-btn-primary">
          Refuerzo final
        </Link>
        <Link to="/avanzo/progreso" className="avz-btn avz-btn-secondary">
          Ver mi progreso
        </Link>
      </div>

      <PostEvalBuddy source="final_simulation" reflectionType="POST_FINAL" />
    </div>
  );
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
}
